import {
  Component,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { RestApiService } from '@core/services/rest-api.service';
import { UserRolesAndPermissionsService } from '@core/services/user-roles-and-permissions.service';
import { Entity } from '@shared/constants/entity';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';
import { FullScreenMapComponent } from '@features/map/full-screen-map/full-screen-map.component';
import { FullScreenMapProps } from '@features/map/interfaces';
import { combineLatest, Observable, of, take } from 'rxjs';
import { PoiGridService } from './components/poi-grid/poi-grid.service';
import { DialogService } from '@core/services/dialog.service';
import { MapId } from '@features/map/constants/map-id.enum';
import { VtSplitterService } from '@shared/services/grid/vt-splitter.service';
import {
  DvSplitterEventsService,
  DvSplitterResizableSide,
} from '@devinco/ui/dds/splitter';
import { TypedPOIService } from './services/typed-poi.service';
import { RouteNetwork } from '@shared/interfaces/route-network';
import { RouteNetworkLayerBuilderService } from '@pages/route-network-page/services/route-network-layer-builder.service';

@Component({
  templateUrl: './poi-page.component.html',
  styleUrls: ['../../styles/page.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PoiPageComponent implements OnInit {
  private api = inject(RestApiService);
  private dialog = inject(DialogService);
  private splitterService = inject(VtSplitterService);
  private splitterEvents = inject(DvSplitterEventsService);
  private poiGridService = inject(PoiGridService);
  private permissionsService = inject(UserRolesAndPermissionsService);
  private poiMarkerService = inject(TypedPOIService);
  private routeNetworkLayerBuilderService = inject(
    RouteNetworkLayerBuilderService
  );

  resizableSide: typeof DvSplitterResizableSide = DvSplitterResizableSide;
  disableMapBtn = false;
  canAddPOI$: Observable<boolean>;

  ngOnInit(): void {
    this.canAddPOI$ = this.permissionsService.hasPermissions([
      RolesAndPermissions.POIEdit,
    ]);
  }

  onCreateClick(): void {
    this.splitterEvents.isSplitterOpened$.next(true);
  }

  onShowAllPoiClick(): void {
    this.disableMapBtn = true;
    combineLatest([
      this.api.getData({
        entity: Entity.POI,
        fields: this.poiGridService.colDefs.map(colDef => colDef.field!),
        filterModel: this.splitterService.agGrid.api.getFilterModel(),
        quickFilterText:
          this.splitterService.agGrid.context?.quickFilterText ||
          this.splitterService.agGrid.gridOptions?.context?.quickFilterText,
      }),
      this.api.getCustom<RouteNetwork[]>('Route/MyNetworks'),
    ]).subscribe(([resp, routeNetworks]) => {
      const data: FullScreenMapProps = {
        id: MapId.PoiFullScreenMap,
        markers$: of([
          { points: this.poiMarkerService.getTypedPOIMarkers(resp.data) },
          ...this.routeNetworkLayerBuilderService.mapRouteNetworkToMapMarkerInput(
            routeNetworks
          ),
        ]),
        useMarkerClustering: true,
      };

      this.dialog
        .open<FullScreenMapProps, FullScreenMapComponent>({
          size: 'full',
          contentComponent: FullScreenMapComponent,
          hideHeader: true,
          hideFooter: true,
          data,
        })
        .closed.pipe(take(1))
        .subscribe(() => (this.disableMapBtn = false));
    });
  }
}
